import {
    ComponentMappingStatus,
    ComponentMappingStatusSummary,
} from '../../../../../@types/component-mapping-state';

/**
 * How a component version's standing against a report source is put into words. Shared by the
 * Mapping tab and the workbook header so the same pair never reads two different ways in two
 * places, and kept free of component imports so it can be tested as the plain logic it is.
 */

/**
 * The status in words, with the numbers that make it actionable. Deliberately says how many
 * reports are involved rather than only naming a state: "partially mapped" on its own does not
 * tell anyone whether the next run takes a minute or a day.
 */
export function describeMappingStatus(status: ComponentMappingStatusSummary): string {
    const total = status.reportsInSource.toLocaleString();
    switch (status.status) {
        case ComponentMappingStatus.MAPPING_IN_PROGRESS:
            return 'Mapping in progress';
        case ComponentMappingStatus.NOT_MAPPED:
            return status.reportsInSource === 0
                ? 'No reports in this source'
                : `Not mapped (${total} reports to evaluate)`;
        case ComponentMappingStatus.PARTIALLY_MAPPED:
            return `Partially mapped (${status.withCurrentRecord.toLocaleString()} of ${total})`;
        case ComponentMappingStatus.CHANGED_SINCE_MAPPING:
            return `${status.changedSinceRecord.toLocaleString()} report`
                + `${status.changedSinceRecord === 1 ? '' : 's'} changed since mapping`;
        default:
            return `Mapped (${total} reports)`;
    }
}

/** The full breakdown behind the status, for a tooltip. */
export function describeMappingStatusDetail(status: ComponentMappingStatusSummary): string {
    return [
        `${status.withCurrentRecord.toLocaleString()} mapped as they now read`,
        `${status.neverEvaluated.toLocaleString()} never evaluated`,
        `${status.failedToEvaluate.toLocaleString()} failed to evaluate`,
        `${status.changedSinceRecord.toLocaleString()} changed since they were evaluated`,
    ].join(', ');
}

/**
 * How many reports the next update run would evaluate: everything without a current record,
 * failures included. A failed report has no usable answer, so the next run has to look at it
 * again, and leaving it out here would say there is nothing to do while reports sit unevaluated.
 */
export function reportsDue(status: ComponentMappingStatusSummary): number {
    return status.neverEvaluated + status.failedToEvaluate + status.changedSinceRecord;
}
